import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Printer, Pencil, Send, Check, X, RotateCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatDate, divisionMeta, SELLER, GST_RATE } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { ActivityTimeline } from "@/components/ActivityTimeline";
import { WonDialog } from "@/components/WonDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const quoteImgUrl = (path) => {
  const t = localStorage.getItem("sc_token");
  return t && path ? `${BACKEND}/api/quotations/image/${path}?auth=${t}` : "";
};

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [decision, setDecision] = useState(null); // 'approve' | 'reject'
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);

  const { data: q, isLoading } = useQuery({
    queryKey: ["quote", id],
    queryFn: async () => (await api.get(`/quotations/${id}`)).data,
  });

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); qc.invalidateQueries({ queryKey: ["quote", id] }); if (msg) toast.success(msg); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const submitDecision = async () => {
    await act(() => api.post(`/quotations/${id}/decision`, { action: decision, comments }), `Quote ${decision === "approve" ? "approved" : "rejected"}`);
    setDecision(null); setComments("");
  };

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!q) return <p className="text-slate-400">Quotation not found.</p>;

  const meta = divisionMeta(q.division_id);
  const taxable = q.total_amount || 0;
  const gst = taxable * GST_RATE / 100;
  const grandIncl = taxable + gst;
  const isApprover = (user?.role === "sales_head" && user?.division_id === q.division_id) || user?.role === "super_admin";
  const editable = ["draft", "rejected", "revised"].includes(q.status) && user?.role !== "factory" && user?.role !== "management";
  const canRevise = ["approved", "sent", "accepted", "rejected"].includes(q.status) && user?.role !== "factory" && user?.role !== "management";

  return (
    <div>
      <div className="flex items-center justify-between mb-4 no-print">
        <button onClick={() => navigate("/quotes")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to Quotes
        </button>
        <div className="flex flex-wrap gap-2 justify-end">
          {editable && <Button variant="outline" className="gap-1.5" onClick={() => navigate(`/quotes/new?quote=${id}`)} data-testid="edit-quote-btn"><Pencil className="h-4 w-4" /> Edit</Button>}
          {q.status === "draft" && <Button className="bg-slate-900 hover:bg-slate-800 gap-1.5" disabled={busy} onClick={() => act(() => api.post(`/quotations/${id}/submit`), "Submitted for approval")} data-testid="submit-btn"><Send className="h-4 w-4" /> Submit for Approval</Button>}
          {isApprover && q.status === "pending_approval" && (
            <>
              <Button className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" onClick={() => setDecision("approve")} data-testid="approve-btn"><Check className="h-4 w-4" /> Approve</Button>
              <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 gap-1.5" onClick={() => setDecision("reject")} data-testid="reject-btn"><X className="h-4 w-4" /> Reject</Button>
            </>
          )}
          {q.status === "approved" && <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" disabled={busy} onClick={() => act(() => api.post(`/quotations/${id}/status`, { status: "sent" }), "Marked as sent")} data-testid="send-btn"><Send className="h-4 w-4" /> Mark Sent</Button>}
          {q.status === "sent" && <Button className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" disabled={busy} onClick={() => {
            if (!q.opportunity_id && q.lead_id) {
              toast.info("Convert the lead to an Opportunity first — the quote moves with it, then accept to mark Order Won");
              return;
            }
            if (q.opportunity && ["Order Won", "Order Booked", "Order Fulfilled"].includes(q.opportunity.stage)) {
              act(() => api.post(`/quotations/${id}/status`, { status: "accepted" }), "Quote accepted");
            } else setAcceptOpen(true);
          }} data-testid="accept-btn"><CheckCircle2 className="h-4 w-4" /> Mark Accepted</Button>}
          {canRevise && <Button variant="outline" className="gap-1.5" disabled={busy} onClick={() => act(async () => { const { data } = await api.post(`/quotations/${id}/revise`); navigate(`/quotes/new?quote=${data.id}`); })} data-testid="revise-btn"><RotateCcw className="h-4 w-4" /> Revise</Button>}
          <Button variant="outline" className="gap-1.5" onClick={() => window.print()} data-testid="print-btn"><Printer className="h-4 w-4" /> Print / PDF</Button>
        </div>
      </div>

      {q.status === "pending_approval" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-4 text-sm text-amber-800 no-print" data-testid="pending-banner">
          This quotation is <strong>pending approval</strong> from the {meta.name} Sales Head.
        </div>
      )}
      {q.status === "rejected" && q.approval_comments && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 mb-4 text-sm text-red-800 no-print">
          <strong>Rejected:</strong> {q.approval_comments}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Printable branded quote */}
        <div className="lg:col-span-2">
          <Card className="overflow-hidden" data-testid="quote-document">
            <div className="px-6 py-5 text-white" style={{ backgroundColor: meta.primary }}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <img src="/uvi-emblem-white.svg" alt="UniVicoustic" className="h-11 w-auto" data-testid="quote-logo" />
                    <img src="/uvi-wordmark-white.svg" alt="UniVicoustic" className="h-4 w-auto" />
                  </div>
                  <p className="font-heading text-2xl font-extrabold tracking-tight">{SELLER.name}</p>
                  <p className="text-sm opacity-90">{SELLER.tagline}</p>
                  <p className="text-xs opacity-90 mt-2 max-w-md leading-relaxed">{SELLER.address}</p>
                  <p className="text-xs opacity-90 mt-1">GSTIN: {SELLER.gstin} · PAN: {SELLER.pan} · State: {SELLER.state} ({SELLER.stateCode})</p>
                  <p className="text-xs opacity-90">{SELLER.phone} · {SELLER.email}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs uppercase tracking-wider opacity-80">Quotation</p>
                  <p className="font-mono font-bold text-lg">{q.quote_number}</p>
                  <p className="text-xs opacity-90">Version {q.version_number}</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="flex justify-between mb-5">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Prepared For</p>
                  <p className="text-sm font-medium text-slate-800 mt-0.5">{q.company}</p>
                  <p className="text-sm text-slate-500">{q.deal_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Date</p>
                  <p className="text-sm text-slate-800 mt-0.5">{formatDate(q.created_at)}</p>
                  <div className="mt-1 no-print"><StatusBadge kind="quote" value={q.status} /></div>
                </div>
              </div>

              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: meta.primary }}>
                    <th className="text-left py-2 text-[11px] uppercase tracking-wider text-slate-500">Product</th>
                    <th className="text-right py-2 text-[11px] uppercase tracking-wider text-slate-500">Qty</th>
                    <th className="text-right py-2 text-[11px] uppercase tracking-wider text-slate-500">List Price (DTP)</th>
                    <th className="text-right py-2 text-[11px] uppercase tracking-wider text-slate-500">Disc%</th>
                    <th className="text-right py-2 text-[11px] uppercase tracking-wider text-slate-500">Final Price</th>
                    <th className="text-right py-2 text-[11px] uppercase tracking-wider text-slate-500">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.line_items || []).map((it, i) => {
                    const lp = it.list_price ?? it.unit_price ?? 0;
                    const fp = it.final_price ?? (lp * (1 - (it.discount_pct || 0) / 100));
                    return (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-2.5 pr-2 font-medium text-slate-800 align-top">
                          {it.product_name}
                          {it.erp_product_code ? <span className="block font-mono text-[10px] text-slate-400 font-normal">Code: {it.erp_product_code}</span> : null}
                          {it.configurator_params?.length > 0 && (
                            <span className="block text-[10px] text-slate-500 font-normal mt-0.5" data-testid={`quote-line-config-params-${i}`}>
                              {it.configurator_params.map((p) => `${p.label}: ${p.value}`).join(" · ")}
                            </span>
                          )}
                          {it.image && (
                            <a href={quoteImgUrl(it.image)} target="_blank" rel="noreferrer" className="block mt-1.5">
                              <img src={quoteImgUrl(it.image_thumb || it.image)} alt="item" className="h-14 max-w-[130px] rounded object-cover border border-slate-200" onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid={`quote-line-image-${i}`} />
                            </a>
                          )}
                        </td>
                        <td className="py-2.5 text-right align-top whitespace-nowrap">{it.qty}{it.unit ? <span className="text-[10px] text-slate-400"> {it.unit}</span> : null}</td>
                        <td className="py-2.5 text-right font-mono align-top">{formatINR(lp)}</td>
                        <td className="py-2.5 text-right align-top">{it.discount_pct || 0}%</td>
                        <td className="py-2.5 text-right font-mono align-top">{formatINR(fp)}</td>
                        <td className="py-2.5 text-right font-mono font-medium align-top">{formatINR(it.line_total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="flex justify-end mt-4">
                <div className="w-72 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono">{formatINR(q.total_amount + q.discount_total)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Discount</span><span className="font-mono text-red-600">- {formatINR(q.discount_total)}</span></div>
                  <div className="flex justify-between border-t pt-1.5 mt-1"><span className="text-slate-500">Taxable Value</span><span className="font-mono">{formatINR(taxable)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">GST @ {GST_RATE}%</span><span className="font-mono">{formatINR(gst)}</span></div>
                  <div className="flex justify-between font-semibold text-base border-t pt-1.5 mt-1" style={{ color: meta.primary }}><span>Grand Total</span><span className="font-mono" data-testid="quote-grand-total">{formatINR(grandIncl)}</span></div>
                </div>
              </div>

              {q.notes && <div className="mt-5 pt-4 border-t"><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Notes & Terms</p><p className="text-sm text-slate-600 whitespace-pre-wrap">{q.notes}</p></div>}

              {q.status === "approved" || q.status === "sent" || q.status === "accepted" ? (
                <div className="mt-5 pt-4 border-t flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> {q.auto_approved ? "Auto-approved (discount within threshold)" : `Approved by ${q.approved_by_name || "Sales Head"}`} on {formatDate(q.approved_at)}
                </div>
              ) : null}
              <div className="mt-6 pt-4 border-t text-[11px] text-slate-400">
                <p>Prices in INR. GST charged as applicable. This is a system-generated quotation and does not require a physical signature.</p>
                <p className="mt-0.5">{SELLER.name} · GSTIN {SELLER.gstin} · {SELLER.jurisdiction}</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-4 no-print">
          <Card className="p-5">
            <h3 className="font-heading font-semibold text-sm text-slate-800 mb-3">Status & Approval</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Status</span><StatusBadge kind="quote" value={q.status} /></div>
              <div className="flex justify-between"><span className="text-slate-500">Created by</span><span>{q.created_by_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-mono font-medium">{formatINR(q.total_amount)}</span></div>
              {q.discount_pct != null && (
                <div className="flex justify-between"><span className="text-slate-500">Discount</span>
                  <span className={`font-medium ${q.discount_pct > 0 ? "text-amber-600" : "text-slate-700"}`}>{q.discount_pct}%{q.discount_pct > 0 ? " · Sales Head approval" : ""}</span>
                </div>
              )}
              {q.approved_by_name && <div className="flex justify-between"><span className="text-slate-500">Decision by</span><span>{q.approved_by_name}</span></div>}
              {q.auto_approved && <div className="flex justify-between"><span className="text-slate-500">Approval</span><span className="text-emerald-700 font-medium">Auto-approved</span></div>}
              {q.approval_comments && <p className="text-xs text-slate-500 pt-2 border-t">“{q.approval_comments}”</p>}
            </div>
          </Card>
          <Card className="p-5">
            <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">Activity</h3>
            <ActivityTimeline activities={q.activities} />
          </Card>
        </div>
      </div>

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader><DialogTitle className="font-heading">{decision === "approve" ? "Approve Quotation" : "Reject Quotation"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">{q.quote_number} · {formatINR(q.total_amount)}</p>
          <Textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} placeholder="Add comments (optional)…" data-testid="decision-comments" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)}>Cancel</Button>
            <Button className={decision === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} disabled={busy} onClick={submitDecision} data-testid="confirm-decision-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}{decision === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WonDialog open={acceptOpen} onOpenChange={setAcceptOpen}
        title="Quote accepted — mark Order Won" confirmLabel="Accept & Mark Order Won" saving={busy}
        onConfirm={(p) => { setAcceptOpen(false); act(() => api.post(`/quotations/${id}/status`, { status: "accepted", ...p }), "Quote accepted — opportunity marked Order Won with the booking value"); }} />
    </div>
  );
}
